/**
 * Lista de traspasos entre almacenes con filtros y acciones
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    Stack,
    Divider,
    useTheme,
    useMediaQuery
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import { DataGrid } from "@mui/x-data-grid";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { useNavigate } from "react-router-dom";

import TraspasosService from "../api/traspasos.api";
import AlmacenesService from "../../../services/almacenes.service";
import UsuariosService from "../../../services/usuarios.service";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";
import { generateTraspasoColumns } from "../helpers/columns";
import { TRASPASO_ESTADO } from "../constants";

import {
    FiltrosTraspaso,
    DrawerDetalle,
    ModalConfirmacion,
    TraspasosMobileList,
} from "../components";
import { applyClientFilters, enrichTraspasoWithNames } from "../helpers/filtros";

// Valores iniciales de fechas para los filtros
const getFechaInicioInicial = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const getFechaFinInicial = () => new Date().toISOString().slice(0, 10);

export default function ListaTraspasoAlmacenes() {
    const navigate = useNavigate();
    const theme = useTheme();

    // Mejor para "responsive real": móvil + tablet
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [showConfirmEstado, setShowConfirmEstado] = useState(false);
    const [rowToChange, setRowToChange] = useState(null);
    const { state, updateState, refrescarContadorTraspasos } = useContext(StateContext);

    const [almacenes, setAlmacenes] = useState([]);

    // Estados de inputs (valores que el usuario escribe/selecciona)
    const [fechaInicio, setFechaInicio] = useState(getFechaInicioInicial);
    const [fechaFin, setFechaFin] = useState(getFechaFinInicial);
    const [estado, setEstado] = useState("");
    const [almacen, setAlmacen] = useState("");
    const [empleado, setEmpleado] = useState("");
    const [q, setQ] = useState("");

    // Estados de filtros aplicados (se actualizan al hacer clic en "Filtrar")
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        almacen: "",
        empleado: "",
        q: ""
    });
    // Filtro rápido por estado desde estadísticas (null = todos)
    const [filtroEstado, setFiltroEstado] = useState(null);

    // Drawer detalle
    const [open, setOpen] = useState(false);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [detalle, setDetalle] = useState(null);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const [emp, a] = await Promise.all([
                UsuariosService.listarUsuarios(),
                AlmacenesService.obtenerAlmacenes(true),
            ]);
            setEmpleados(emp ?? []);
            setAlmacenes(a ?? []);

            // Cargar traspasos con filtros iniciales
            await aplicarFiltros();
        } finally {
            setLoading(false);
        }
    };

    // DECISION: useCallback para estabilizar la referencia de estas funciones.
    // columnsDesktop y columnsMobile dependen de ellas vía useMemo; sin useCallback
    // cada render genera una nueva referencia y provoca que useMemo recalcule las
    // columnas en cada ciclo, lo que dispara re-renders infinitos en el DataGrid.
    const abrirConfirmEstado = useCallback((row) => {
        setRowToChange(row);
        setShowConfirmEstado(true);
    }, []);

    const cerrarConfirmEstado = useCallback(() => {
        setShowConfirmEstado(false);
        setRowToChange(null);
    }, []);

    const confirmarCambioEstado = async () => {
        if (!rowToChange) return;

        try {
            setLoading(true);

            await TraspasosService.update(rowToChange.id, {
                estado: TRASPASO_ESTADO.TRASPASADO,
            });

            // Refrescar contador del header inmediatamente
            refrescarContadorTraspasos();

            // Si el traspaso que se acaba de marcar es el que está abierto en el drawer,
            // actualizamos el detalle local para que el botón desaparezca sin cerrar el panel.
            if (detalle && String(detalle.id) === String(rowToChange.id)) {
                setDetalle((prev) => ({ ...prev, estado: TRASPASO_ESTADO.TRASPASADO }));
            }

            cerrarConfirmEstado();
            await aplicarFiltros();
        } catch (err) {
            console.error(err);
            updateState({
                toasts: [...state.toasts, Toasts.generateToast("Error al marcar el traspaso.", "error")],
            });
        } finally {
            setLoading(false);
        }
    };

    const rowsView = useMemo(() => {
        if (!rows.length) return [];

        // Aplicar filtros del lado del cliente
        const filtered = applyClientFilters(rows, filtrosAplicados, almacenes, empleados);

        // Agregar campos calculados (nombres resueltos)
        return filtered.map((r) => enrichTraspasoWithNames(r, almacenes, empleados));
    }, [rows, almacenes, empleados, filtrosAplicados]);

    // Estadísticas de traspasos
    const estadisticas = useMemo(() => {
        const pendientes = rowsView.filter(t => t.estado?.toUpperCase() === TRASPASO_ESTADO.PENDIENTE).length;
        const traspasados = rowsView.filter(t => t.estado?.toUpperCase() === TRASPASO_ESTADO.TRASPASADO).length;
        const total = rowsView.length;
        return { pendientes, traspasados, total };
    }, [rowsView]);

    // Traspasos filtrados según el filtro de estado activo
    const rowsViewFiltrados = useMemo(() => {
        if (!filtroEstado) return rowsView;
        return rowsView.filter(t => t.estado?.toUpperCase() === filtroEstado);
    }, [rowsView, filtroEstado]);

    const aplicarFiltros = async () => {
        setLoading(true);
        try {
            // Cargar datos desde el backend con filtros de fecha y estado
            const data = await TraspasosService.list({
                fechaDesde: fechaInicio,
                fechaHasta: fechaFin,
                estado,
            });

            setRows(
                (data ?? []).map((t) => ({
                    id: t.id,
                    ...t,
                }))
            );

            // Aplicar filtros del lado del cliente (almacén, empleado y búsqueda)
            setFiltrosAplicados({
                almacen: almacen,
                empleado: empleado,
                q: q
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLimpiar = async () => {
        const fechaInicioInicial = getFechaInicioInicial();
        const fechaFinInicial = getFechaFinInicial();

        setFechaInicio(fechaInicioInicial);
        setFechaFin(fechaFinInicial);
        setAlmacen('');
        setEmpleado('');
        setEstado('');
        setQ('');
        setFiltroEstado(null);

        // Aplicar filtros inmediatamente con valores iniciales
        setLoading(true);
        try {
            const data = await TraspasosService.list({
                fechaDesde: fechaInicioInicial,
                fechaHasta: fechaFinInicial,
                estado: '',
            });

            setRows(
                (data ?? []).map((t) => ({
                    id: t.id,
                    ...t,
                }))
            );

            setFiltrosAplicados({
                almacen: '',
                empleado: '',
                q: ''
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEstadisticaClick = async (estadoValor) => {
        // Si ya está activo ese filtro, lo quita
        if (filtroEstado === estadoValor) {
            setEstado('');
            setFiltroEstado(null);
            // Recargar sin filtro de estado
            setLoading(true);
            try {
                const data = await TraspasosService.list({
                    fechaDesde: fechaInicio,
                    fechaHasta: fechaFin,
                    estado: '',
                });
                setRows(
                    (data ?? []).map((t) => ({
                        id: t.id,
                        ...t,
                    }))
                );
            } finally {
                setLoading(false);
            }
        } else {
            // Aplica el nuevo filtro y limpia el campo de estado
            setEstado('');
            setFiltroEstado(estadoValor);
            // Recargar con filtro de estado
            setLoading(true);
            try {
                const data = await TraspasosService.list({
                    fechaDesde: fechaInicio,
                    fechaHasta: fechaFin,
                    estado: estadoValor,
                });
                setRows(
                    (data ?? []).map((t) => ({
                        id: t.id,
                        ...t,
                    }))
                );
            } finally {
                setLoading(false);
            }
        }
    };

    const abrirDetalle = useCallback(async (row) => {
        setOpen(true);
        setDetalle(null);
        setLoadingDetalle(true);
        try {
            setDetalle(row);
        } finally {
            setLoadingDetalle(false);
        }
    }, []);

    const editarTraspaso = useCallback((row) => {
        navigate(`/traspasos/editar/${row.id}`);
    }, [navigate]);

    const generateColumns = useCallback(
        () => generateTraspasoColumns({ onVerDetalle: abrirDetalle, onEditar: editarTraspaso, onConfirmarEstado: abrirConfirmEstado }),
        [abrirDetalle, editarTraspaso, abrirConfirmEstado]
    );

    return (
        <Box id="traspasos" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", sm: "center" }}
                        spacing={2}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <SyncAltIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Traspasos
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Gestiona los traspasos entre almacenes
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate("/traspasos/nuevo")}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Nuevo traspaso
                        </Button>
                    </Stack>

                    {/* Estadísticas */}
                    {!loading && rows.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, 1fr)",
                                        sm: "repeat(3, 1fr)",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                {/* Pendientes */}
                                <Box
                                    onClick={() => handleEstadisticaClick(TRASPASO_ESTADO.PENDIENTE)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.75, sm: 1 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: "#fff3cd",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtroEstado === TRASPASO_ESTADO.PENDIENTE ? "2px solid #ffc107" : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <PendingActionsIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: "#ffc107",
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Pendientes
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: "#856404",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {estadisticas.pendientes}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Traspasados */}
                                <Box
                                    onClick={() => handleEstadisticaClick(TRASPASO_ESTADO.TRASPASADO)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.75, sm: 1 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: "#d1e7dd",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtroEstado === TRASPASO_ESTADO.TRASPASADO ? "2px solid #198754" : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: "#198754",
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, sm: 13 },
                                                fontWeight: 600,
                                                color: "#495057",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Traspasados
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 16, sm: 13 },
                                                fontWeight: 700,
                                                color: "#198754",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.traspasados}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Total */}
                                <Box
                                    onClick={() => handleEstadisticaClick(null)}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 0.5, sm: 0.75 },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 1.5, sm: 1.25 },
                                        borderRadius: 1,
                                        backgroundColor: "#e7f1ff",
                                        gridColumn: { xs: "1 / -1", sm: "auto" },
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                        border: filtroEstado === null ? "2px solid #0d6efd" : "2px solid transparent",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <InventoryIcon
                                        sx={{
                                            fontSize: { xs: 24, sm: 22 },
                                            color: "#0d6efd",
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, sm: 14 },
                                                fontWeight: 600,
                                                color: "#0d6efd",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Total
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 16 },
                                                fontWeight: 700,
                                                color: "#0d6efd",
                                                lineHeight: 1,
                                            }}
                                        >
                                            {estadisticas.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Filtros */}
                    {!loading && rows.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <FiltrosTraspaso
                                fechaInicio={fechaInicio}
                                setFechaInicio={setFechaInicio}
                                fechaFin={fechaFin}
                                setFechaFin={setFechaFin}
                                almacen={almacen}
                                setAlmacen={setAlmacen}
                                empleado={empleado}
                                setEmpleado={setEmpleado}
                                q={q}
                                setQ={setQ}
                                almacenesOptions={almacenes}
                                empleadosOptions={empleados}
                                onFiltrar={aplicarFiltros}
                                onLimpiar={handleLimpiar}
                                loading={loading}
                                totalResultados={rowsViewFiltrados.length}
                                fechaInicioInicial={getFechaInicioInicial()}
                                fechaFinInicial={getFechaFinInicial()}
                            />
                        </>
                    )}
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {isMobile ? (
                        <TraspasosMobileList
                            rows={rowsViewFiltrados}
                            loading={loading}
                            onVer={abrirDetalle}
                            onEditar={editarTraspaso}
                            onTraspasar={abrirConfirmEstado}
                        />
                    ) : (
                        <DataGrid
                            rows={rowsViewFiltrados}
                            columns={generateColumns()}
                            loading={loading}
                            autoHeight
                            disableRowSelectionOnClick
                            onRowDoubleClick={(p) => abrirDetalle(p.row)}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            initialState={DATA_GRID_INITIAL_STATE}
                            density="standard"
                            rowHeight={52}
                            columnHeaderHeight={56}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={{
                                ...DATA_GRID_SX,
                                "& .MuiDataGrid-cell": {
                                    ...DATA_GRID_SX["& .MuiDataGrid-cell"],
                                    py: 1,
                                    whiteSpace: "normal",
                                    lineHeight: 1.2,
                                },
                                "& .MuiDataGrid-row": { maxHeight: "none !important" },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                                    outline: "none",
                                },
                            }}
                            localeText={DATA_GRID_LOCALE_TEXT}
                        />
                    )}
                </Box>
            </Card>

            {/* Drawer detalle */}
            <DrawerDetalle
                open={open}
                onClose={() => setOpen(false)}
                detalle={detalle}
                loading={loadingDetalle}
                onMarcarTraspasado={abrirConfirmEstado}
            />

            {/* Modal confirmación */}
            <ModalConfirmacion
                show={showConfirmEstado}
                onHide={cerrarConfirmEstado}
                rowToChange={rowToChange}
                onConfirm={confirmarCambioEstado}
                loading={loading}
            />
        </Box>
    );
}
